import { t } from "@transifex/native";

/**
 * Error name marking a message that belongs on the sign-up phone field rather than
 * in a toast. A field error clears as soon as the user edits the number, whereas a
 * toast lingers and ends up contradicting a value they already corrected.
 */
export const PHONE_FIELD_ERROR = "PhoneFieldError";

/**
 * Codes the backend's Cognito PreSignUp trigger reports on rejection. The Lambda
 * contract only carries a string, so Cognito surfaces the code embedded in a
 * UserLambdaValidationException message, e.g.
 * "PreSignUp failed with error PhoneNumberInUse: An account with this phone number already exists."
 *
 * The first match wins, so no code may be a substring of another one in this
 * list. Keep that invariant if codes are added later.
 */
const PRE_SIGN_UP_PHONE_ERRORS: { code: string; message: () => string }[] = [
  { code: "PhoneNumberInUse", message: () => t("This phone number is already in use by another account.") },
  { code: "PhoneNumberRequired", message: () => t("Phone number is required") },
  { code: "InvalidPhoneNumber", message: () => t("Please provide a valid phone number") },
];

/**
 * Returns the message for the PreSignUp phone code embedded in a
 * UserLambdaValidationException message, or undefined when it carries none of
 * them.
 *
 * A miss is expected, not exceptional: the fail-closed uniqueness check in the
 * trigger also rejects with a UserLambdaValidationException carrying none of
 * these codes. Callers must fall through to their generic error handling rather
 * than guessing at a phone error.
 */
export const getPreSignUpPhoneErrorMessage = (exceptionMessage: string): string | undefined =>
  PRE_SIGN_UP_PHONE_ERRORS.find(({ code }) => exceptionMessage.includes(code))?.message();
